import React, {
  useState, useEffect, useRef, useCallback, useContext,
} from 'react';
import { useFormContext } from 'react-hook-form';
import EditorJS, { API, LogLevels } from '@editorjs/editorjs';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';

import EDITOR_TOOLS from './tools/EditorTools';
import useStyles from './styles';
import {
  parseJsonToEditorState,
  onKeyEditorHandler,
  editorMenuObserver,
  addInstructionsToData,
  parseJsonToEditorStateWithInstruction,
} from '../../services/editorJS/utils';
import PasteListener from '../../services/editorJS/event-listeners/PasteListener';
import KeyboardEventListener from '../../services/editorJS/event-listeners/KeyboardEventListener';

import InstructionTooltip from '../../services/editorJS/InstructionTooltip';
import { selectProject } from '../../store/modules/projects/selectors';
import {
  saveScenesRequest,
  setEditingScript,
  setSavedScript,
  setSavingScript,
} from '../../store/modules/scenes/actions';
import { notify } from '../../store/modules/notifications/actions';
import InstructionContainer from './tools/InstructionContainer';
import { InstructionEvent } from './tools/types';
import AutoSaveComponent from './AutoSaveComponent';
import {
  addInstructionEventByEditor,
  updateAllInstructionActionEvents,
} from './tools/utils';
import { ScriptEditorJSContext } from '../../views/projects/tab-scripts/ScriptContext';
import { ScriptInstructions } from '../../types/instructions';
import UndoAndRedoListener from '../../services/editorJS/event-listeners/UndoAndRedoListener';

type Props = {
  name: string;
  canEdit?: boolean | null;
};

/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars */
let PREVENT_ON_CHANGE = false;

const ScriptEditor = ({ name, canEdit = null }:Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const {
    setValue, handleSubmit, watch, getValues,
  } = useFormContext();
  const dataValue = watch(name);
  const [data, setData] = useState(parseJsonToEditorState(dataValue));
  const [instanceEditor, setInstanceEditor] = useState<EditorJS | undefined>();
  const refEditor = useRef<HTMLDivElement>(null);
  const currentProject = useSelector(selectProject);
  const { setInstanceFromEditorJs } = useContext(ScriptEditorJSContext);

  const editingHandler = useCallback(() => {
    dispatch(setEditingScript(true));
    dispatch(setSavingScript(false));
    dispatch(setSavedScript(false));
  }, [dispatch]);

  useEffect(() => {
    if (refEditor.current && instanceEditor) {
      const keyboardHandler = new KeyboardEventListener({
        refEditor: refEditor.current,
        data,
        editor: instanceEditor,
        dispatch,
      });

      keyboardHandler.init();

      return () => {
        keyboardHandler.destroy();
      };
    }
  },
  [data, refEditor, instanceEditor]);

  useEffect(() => {
    const editor = refEditor.current;

    if (editor !== null && instanceEditor) {
      const pasteListener = new PasteListener({
        editor: instanceEditor,
        refEditor: editor,
      });
      pasteListener.init();

      return () => {
        pasteListener.destroy();
      };
    }
  }, [refEditor, instanceEditor]);

  useEffect(() => {
    PREVENT_ON_CHANGE = false;
  }, [dispatch]);

  useEffect(() => {
    if (instanceEditor === undefined && refEditor.current !== null && typeof canEdit === 'boolean') {
      const editor = new EditorJS({
        tools: EDITOR_TOOLS,
        holder: refEditor.current,
        data,
        autofocus: true,
        logLevel: 'ERROR' as LogLevels,
        onChange(api: API): void {
          UndoAndRedoListener.addState();
        },
        onReady(): void {
          UndoAndRedoListener.interimState = {
            caretPosition: 0,
            blockIndex: 0,
            data: data.blocks as any,
          };
          UndoAndRedoListener.addState();

          addInstructionEventByEditor(
            editor,
            InstructionEvent.SET_PREVENT_ON_CHANGE_EDITOR,
            (value) => {
              PREVENT_ON_CHANGE = value;
            },
          );

          addInstructionEventByEditor(
            editor,
            InstructionEvent.UPDATE_INLINE_ACTION_EVENT_LISTENERS,
            () => {
              // @ts-ignore
              updateAllInstructionActionEvents(editor, refEditor.current);
            },
          );

          addInstructionEventByEditor(
            editor,
            InstructionEvent.NOTIFY,
            (notification) => {
              dispatch(notify(notification));
            },
          );

          addInstructionEventByEditor(
            editor,
            InstructionEvent.ADD_INSTRUCTIONS_TO_DATA,
            (instructions) => {
              const data = addInstructionsToData(getValues()[name], instructions);
              setValue(name, data);
            },
          );


          addInstructionEventByEditor(
            editor,
            InstructionEvent.SUBMIT_DATA,
            (data:{editorText?: string}) => {
              editor.save().then((outputData) => {
                editingHandler();
                const actualInstructions: ScriptInstructions = [];
                const fieldData = parseJsonToEditorStateWithInstruction(getValues()[name]);
                fieldData.blocks = outputData.blocks;

                const blockString = JSON.stringify(outputData.blocks);
                fieldData.instructions.forEach(
                  (item) => {
                    if (blockString.indexOf(item.id) !== -1) {
                      actualInstructions.push(item);
                    }
                  },
                );
                fieldData.instructions = actualInstructions;
                setData(fieldData);
                if (data.editorText) {
                  setValue(name, data.editorText);
                } else {
                  setValue(name, JSON.stringify(fieldData));
                }
                handleSubmit((submitData) => {
                  dispatch(saveScenesRequest(submitData as any));
                })();
                PREVENT_ON_CHANGE = false;
              });
            },
          );

          editorMenuObserver(editor);

          if (!canEdit) {
            document.querySelectorAll('[contenteditable=true]').forEach((item) => {
              item.setAttribute('contenteditable', 'false');
            });
          }
          setInstanceFromEditorJs(editor);
        },
      });
      window.scriptEditorRef = refEditor.current;
      setInstanceEditor(editor);
    }
  }, [
    instanceEditor,
    setInstanceEditor,
    data,
    setData,
    refEditor,
    name,
    setValue,
    canEdit,
    getValues,
    editingHandler,
    setInstanceFromEditorJs,
  ]);

  useEffect(() => {
    if (refEditor.current) {
      const instructionTooltip = new InstructionTooltip({
        refEditor: refEditor.current,
        params: {
          project: currentProject.data,
        },
      });
      instructionTooltip.init();

      return () => instructionTooltip.destroy();
    }
  }, [refEditor, currentProject]);


  return (
    <>
      <Grid container justify="flex-end" alignItems="center" style={{ padding: '0 10px', height: 22 }}>
        <AutoSaveComponent instanceEditor={instanceEditor} name={name} />
      </Grid>
      <div
        ref={refEditor}
        className={classes.editor}
        data-intercom-target="Script edit"
        onKeyUp={onKeyEditorHandler(instanceEditor)}
      />
      <InstructionContainer instanceEditor={instanceEditor} refEditor={refEditor.current} />
    </>
  );
};

export default ScriptEditor;
